import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
// Admin icons
import AdminPanelSettingsRounded from '@mui/icons-material/AdminPanelSettingsRounded';
import ApartmentRounded from '@mui/icons-material/ApartmentRounded';
import ApiRounded from '@mui/icons-material/ApiRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import BadgeRounded from '@mui/icons-material/BadgeRounded';
import BookmarkBorderRounded from '@mui/icons-material/BookmarkBorderRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ContactPageRounded from '@mui/icons-material/ContactPageRounded';
// Client icons
import DashboardRounded from '@mui/icons-material/DashboardRounded';
import DeleteSweepRounded from '@mui/icons-material/DeleteSweepRounded';
import FormatListBulletedRounded from '@mui/icons-material/FormatListBulletedRounded';
import GroupRounded from '@mui/icons-material/GroupRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import PersonOffRounded from '@mui/icons-material/PersonOffRounded';
import RateReviewRounded from '@mui/icons-material/RateReviewRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import SellRounded from '@mui/icons-material/SellRounded';
import SmartToyRounded from '@mui/icons-material/SmartToyRounded';
import SpellcheckRounded from '@mui/icons-material/SpellcheckRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { APP_VERSION } from '@/config/app';
// The store files, not the feature's index: the index re-exports the page,
// which must stay in its own lazy chunk.
import { pendingCountRequested, selectPendingClientImports } from '@/features/list/client-import-alerts';
import { openCountRequested } from '@/features/list/list-feedbacks/store/list-feedbacks.actions';
import { selectOpenCount } from '@/features/list/list-feedbacks/store/list-feedbacks.selectors';
import { issueCountRequested } from '@/features/profile/profile-cleaning/store/profile-cleaning.actions';
import {
  selectIssueCount,
  selectToCompleteCount,
} from '@/features/profile/profile-cleaning/store/profile-cleaning.selectors';
import { paths } from '@/shared/routes/paths';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import {
  cleaningAccessibleLabel,
  cleaningTooltipLines,
  isNavPathActive,
  NAV_COUNT_WORDING,
  type NavCountKey,
  navCountLabel,
  type NavItem,
  type NavSection,
} from './side-nav-logic';

export const SIDENAV_WIDTH_EXPANDED = 248;

/**
 * The height of the shell's top band: the TopBar, and the SideNav's brand bar.
 *
 * One constant because they are one band. They were 64px and `pt: 2` before,
 * which is two rhythms meeting at the seam.
 */
export const SHELL_BAR_HEIGHT = 52;

/**
 * The height of the SideNav's foot (admin mode + version), border included.
 *
 * Fixed so a page's own bottom bar (New List's actions) can be the same band
 * and meet the nav's foot on one line, as the TopBar meets its brand bar.
 */
export const SHELL_FOOT_HEIGHT = 88;

/**
 * The corner on a nav row, in pixels: deliberately not an sx `borderRadius`
 * number.
 *
 * `borderRadius: 1.5` does not mean 1.5px, it means `shape.borderRadius * 1.5`,
 * and this theme's shape is 12, so every row was drawing an 18px corner on a
 * 32px box. That reads as a lozenge, not a selected row, and it was the single
 * loudest thing left in the nav.
 */
const ROW_RADIUS = '8px';
export const SIDENAV_WIDTH_COLLAPSED = 72;

// ─── Client nav sections ──────────────────────────────────────────────────────
const CLIENT_NAV_SECTIONS: NavSection[] = [
  {
    items: [{ label: 'Dashboard', path: paths.home, icon: <DashboardRounded /> }],
  },
  {
    title: 'Searches',
    items: [
      { label: 'Find new LPs', icon: <SearchRounded />, disabled: true, comingSoon: true },
      { label: 'HNWIs', path: paths.profiles.all, icon: <PeopleAltRounded /> },
    ],
  },
  {
    title: 'LP Management',
    items: [
      {
        label: 'Tracked LPs',
        path: paths.trackedLps,
        icon: <BookmarkBorderRounded />,
        tooltip: 'LPs you want to monitor closely',
      },
      {
        label: 'Discovered LPs',
        path: paths.lists.root,
        icon: <FormatListBulletedRounded />,
        tooltip: 'LPs added by Invyo to enrich your database',
      },
      { label: 'Imports', path: paths.imports.root, icon: <UploadFileRounded /> },
    ],
  },
  {
    title: 'Integrations',
    items: [
      {
        label: 'API',
        icon: <ApiRounded />,
        disabled: true,
        comingSoon: true,
        tooltip: 'Enables secure and automated data exchange with your systems.',
      },
      {
        label: 'MCP',
        icon: <SmartToyRounded />,
        disabled: true,
        comingSoon: true,
        tooltip: 'Enables AI models to connect with your tools and data sources.',
      },
    ],
  },
  {
    title: 'Misc',
    items: [
      {
        label: 'Fundraising rounds',
        path: paths.fundraisings.root,
        icon: <AccountBalanceRounded />,
      },
      // Always there, even before anything is published: the page says so (#735, board E4).
      { label: 'FAQ', path: paths.faq, icon: <HelpOutlineRounded /> },
    ],
  },
];

// ─── Admin nav sections ───────────────────────────────────────────────────────
const ADMIN_NAV_SECTIONS: NavSection[] = [
  {
    // Untitled, like the client's Dashboard. No page behind it yet, so it
    // renders as a "Soon" row with no path.
    items: [{ label: 'Dashboard', icon: <DashboardRounded />, disabled: true, comingSoon: true }],
  },
  {
    // "Client management" is the client's own wording for this group (#557). It
    // reads a little wide for Administrators, which is Invyo's own staff and not
    // a client; logged as a product issue rather than moved here, since which
    // section that entry belongs to was not part of the request.
    title: 'Client management',
    items: [
      { label: 'Licences', path: paths.admin.licences.root, icon: <BadgeRounded /> },
      { label: 'Users', path: paths.admin.users.root, icon: <GroupRounded /> },
      {
        label: 'Administrators',
        path: paths.admin.administrators.root,
        icon: <AdminPanelSettingsRounded />,
      },
    ],
  },
  // Collection is where the data comes in, so it opens the section; what it
  // brings in (profiles, companies, schools) follows it.
  {
    title: 'Data Operations',
    items: [
      // The badge (#748) counts the client imports still waiting for Invyo
      // (draft), the same for every admin. It sits here because a client
      // import is a list of Collection; the PO may want it elsewhere.
      {
        label: 'Collection',
        path: paths.admin.lists.root,
        icon: <FormatListBulletedRounded />,
        countKey: 'pendingClientImports',
      },
      // Right after Collection (#717): what clients say about the lists it
      // delivers. The badge is the number still Open, the same for every admin.
      {
        label: 'List Feedbacks',
        path: paths.admin.listFeedbacks,
        icon: <RateReviewRounded />,
        countKey: 'openFeedbacks',
      },
      // Sparkles, not the beaker it used to be: a flask reads "lab /
      // experimental", and this is a production data operation.
      { label: 'Enrichment', path: paths.admin.enrichment.root, icon: <AutoAwesomeRounded /> },
      { label: 'Profiles', path: paths.admin.profiles.root, icon: <ContactPageRounded /> },
      { label: 'Companies', path: paths.admin.companies.root, icon: <ApartmentRounded /> },
      { label: 'Schools', path: paths.admin.schools, icon: <SchoolRounded /> },
      // The badge (#753) is the page's "All issues" figure with no type filter,
      // the same for every admin. Its tooltip (#754) also names the "Exits &
      // investments to complete" queue, which the badge leaves out.
      {
        label: 'Profile Cleaning',
        path: paths.admin.profileCleaning,
        icon: <DeleteSweepRounded />,
        countKey: 'cleaningIssues',
      },
      {
        label: 'Deactivated Profiles',
        path: paths.admin.profiles.deactivated,
        icon: <PersonOffRounded />,
      },
    ],
  },
  {
    title: 'Tools',
    items: [
      { label: 'API Keys', path: paths.admin.api.root, icon: <ApiRounded /> },
      { label: 'Name Rules', path: paths.admin.nameRules, icon: <SpellcheckRounded /> },
      { label: 'FAQ', path: paths.admin.faq, icon: <HelpOutlineRounded /> },
      // A tag, because that is what the rules produce: the labels a profile
      // carries.
      { label: 'Profile types', path: paths.admin.profileTypes, icon: <SellRounded /> },
    ],
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────
interface SideNavProps {
  collapsed: boolean;
  isAdmin: boolean;
  onToggleCollapse: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function SideNav({ collapsed, isAdmin, onToggleCollapse }: SideNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminMode = location.pathname.startsWith('/admin');
  /** Which half of `palette.accent` this zone paints itself with. */
  const accentKey = isAdminMode ? 'admin' : 'client';
  const sections = useMemo(
    () => (isAdminMode ? ADMIN_NAV_SECTIONS : CLIENT_NAV_SECTIONS),
    [isAdminMode],
  );

  const navPaths = useMemo(
    () => sections.flatMap((s) => s.items.flatMap((i) => (i.path ? [i.path] : []))),
    [sections],
  );

  const isActive = (path: string) =>
    isNavPathActive(path, { pathname: location.pathname, navPaths, prefix: isAdminMode });

  // The List Feedbacks (#717), Collection (#748) and Profile Cleaning (#753)
  // badges, refreshed on every admin page change: a feedback posted, a file
  // imported or a profile to clean while an admin works elsewhere shows up on
  // their next click, without polling. A feedback status change on its page
  // refreshes that badge too, and so do the Profile Cleaning page's loads and
  // actions (their sagas).
  const dispatch = useAppDispatch();
  const openFeedbacks = useAppSelector(selectOpenCount);
  const pendingClientImports = useAppSelector(selectPendingClientImports);
  const cleaningIssues = useAppSelector(selectIssueCount);
  const cleaningToComplete = useAppSelector(selectToCompleteCount);
  const showsCounts = isAdmin && isAdminMode;
  useEffect(() => {
    if (!showsCounts) return;
    dispatch(openCountRequested());
    dispatch(pendingCountRequested());
    dispatch(issueCountRequested());
  }, [dispatch, showsCounts, location.pathname]);
  const counts: Record<NavCountKey, number | null> = {
    openFeedbacks,
    pendingClientImports,
    cleaningIssues,
  };

  const renderItem = (item: NavItem) => {
    const active = item.path ? isActive(item.path) : false;
    const countLabel = item.countKey && showsCounts ? navCountLabel(counts[item.countKey]) : null;
    const countText = countLabel && item.countKey ? `${countLabel} ${NAV_COUNT_WORDING[item.countKey]}` : null;
    const linkProps = item.path && !item.disabled ? { component: RouterLink, to: item.path } : {};

    const labelNode = !collapsed && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
        <ListItemText
          primary={item.label}
          slotProps={{
            primary: {
              noWrap: true,
              sx: {
                fontSize: '0.8125rem',
                fontWeight: active ? 600 : 500,
                // The active button below sets the colour; the label inherits it.
                color: 'inherit',
              },
            },
          }}
        />
        {/* No (i) here any more. Some rows carried one, some a "Soon" chip, some
            both and most neither, so the right edge came out ragged: eleven
            items and four different shapes. The tooltip is on the row itself
            now: the same text, a target eight times the size, and one shape. */}
        {countLabel && (
          <Box
            component="span"
            aria-label={countText ?? undefined}
            sx={{
              flexShrink: 0,
              minWidth: 20,
              height: 18,
              px: 0.75,
              borderRadius: '9px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.6875rem',
              fontWeight: 700,
              lineHeight: 1,
              bgcolor: (t) => t.palette.accent[accentKey].main,
              color: (t) => t.palette.getContrastText(t.palette.accent[accentKey].main),
            }}
          >
            {countLabel}
          </Box>
        )}
        {item.comingSoon && (
          <Chip
            label="Soon"
            size="small"
            sx={{
              height: 16,
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              bgcolor: (t) => t.palette.accent[accentKey].bg,
              color: (t) => t.palette.accent[accentKey].main,
              flexShrink: 0,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Box>
    );

    // Collapsed, the Profile Cleaning tooltip is markup, never a label: the row
    // names itself (#754), the way a string tooltip names the other rows.
    const ariaLabel =
      collapsed && item.countKey === 'cleaningIssues' && showsCounts
        ? cleaningAccessibleLabel(item.label, cleaningIssues, cleaningToComplete)
        : undefined;

    const button = (
      <ListItemButton
        disabled={item.disabled}
        aria-current={active ? 'page' : undefined}
        // Only when there is one: an `aria-label: undefined` key would override
        // the name a text tooltip gives the other rows (MUI merges the child's
        // props last).
        {...(ariaLabel !== undefined && { 'aria-label': ariaLabel })}
        {...linkProps}
        sx={{
          borderRadius: ROW_RADIUS,
          mb: 0.25,
          minHeight: 32,
          py: 0.5,
          px: 1.25,
          justifyContent: collapsed ? 'center' : 'flex-start',
          textDecoration: 'none',
          // One active state for both zones. It used to be two spreads, each
          // reaching for a different source: `primary.main` in the client,
          // ADMIN_PALETTE in /admin, which is how only one of them got fixed.
          ...(active && {
            bgcolor: (t) => t.palette.accent[accentKey].bg,
            color: (t) => t.palette.accent[accentKey].main,
            '& .MuiListItemIcon-root': { color: (t) => t.palette.accent[accentKey].main },
          }),
          '&:hover': {
            bgcolor: (t) => (active ? t.palette.accent[accentKey].hover : t.palette.action.hover),
          },
          '&.Mui-disabled': { opacity: 0.4 },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 30,
            mr: collapsed ? 0 : undefined,
            justifyContent: 'center',
            color: 'text.secondary',
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {/* Collapsed, the label and its count are gone: a dot on the icon
              says there is something waiting, the tooltip says how much. */}
          {collapsed && countLabel ? (
            <Badge variant="dot" color="error" overlap="circular">
              {item.icon}
            </Badge>
          ) : (
            item.icon
          )}
        </ListItemIcon>
        {labelNode}
      </ListItemButton>
    );

    // Collapsed, the label is the tooltip's job too; there is nothing else to
    // read. Expanded, only the rows that explain something get one. Profile
    // Cleaning (#754) says its two figures on two lines, in both states.
    const cleaningLines =
      item.countKey === 'cleaningIssues' && showsCounts
        ? cleaningTooltipLines({
            label: item.label,
            issues: cleaningIssues,
            toComplete: cleaningToComplete,
            collapsed,
          })
        : null;
    const tooltipTitle = cleaningLines
      ? cleaningLines.length > 0 && (
          <>
            {cleaningLines.map((line) => (
              <Box key={line} component="span" sx={{ display: 'block' }}>
                {line}
              </Box>
            ))}
          </>
        )
      : collapsed
        ? [countText ? `${item.label} (${countText})` : item.label, item.tooltip]
            .filter(Boolean)
            .join(': ')
        : (item.tooltip ?? '');

    return (
      <Box key={item.label}>
        {tooltipTitle ? (
          <Tooltip
            title={tooltipTitle}
            placement="right"
            arrow
            // Expanded, the Profile Cleaning tooltip is markup that adds to the
            // visible label: a description, never the row's name (#754).
            describeChild={cleaningLines !== null && !collapsed}
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </Box>
    );
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? SIDENAV_WIDTH_COLLAPSED : SIDENAV_WIDTH_EXPANDED,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? SIDENAV_WIDTH_COLLAPSED : SIDENAV_WIDTH_EXPANDED,
          boxSizing: 'border-box',
          bgcolor: (t) => t.palette.surface.chrome,
          backdropFilter: 'blur(12px)',
          borderRightWidth: 1,
          borderRightStyle: 'solid',
          borderRightColor: (t) => t.palette.surface.chromeBorder,
          transition: 'width 0.2s ease-in-out, background-color 0.45s ease',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/*
        ── Brand ──
        SHELL_BAR_HEIGHT, and the same bottom rule as the TopBar. The logo used
        to float on `pt: 2` with a 24px margin under it while the bar next to it
        was 64px tall: two vertical rhythms meeting at the seam, which is a good
        part of why the top of the app never read as one object.
      */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: SHELL_BAR_HEIGHT,
          flexShrink: 0,
          px: collapsed ? 0 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: (t) => `1px solid ${t.palette.surface.chromeBorder}`,
        }}
      >
        {/*
          Collapsed, the logo has the rail to itself and is centred by having
          nothing to share the row with. It stays a logo: it was briefly the
          expand control, which centred it but asked people to guess: a
          clickable mark reads as "go home" everywhere else on the web.
        */}
        <Box
          component="img"
          src="/logo.svg"
          alt="Invyo"
          sx={{
            width: collapsed ? 30 : 32,
            height: 'auto',
            flexShrink: 0,
            transition: 'width 0.2s ease',
          }}
        />
        {!collapsed && (
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
            }}
          >
            Invyo
          </Typography>
        )}

        {/* ADMIN badge: in the flow now, so it cannot land under the toggle */}
        {isAdminMode && !collapsed && (
          <Chip
            icon={<AdminPanelSettingsRounded sx={{ fontSize: '0.7rem !important' }} />}
            label={collapsed ? undefined : 'ADMIN'}
            size="small"
            sx={{
              // A tint, not a slab. Filled amber with dark ink made the badge the
              // brightest object in the chrome, louder than the page title, and
              // shouting a fact you only need to be able to check.
              bgcolor: (t) => t.palette.accent.admin.bg,
              color: (t) => t.palette.accent.admin.main,
              borderRadius: '5px',
              fontWeight: 700,
              fontSize: '0.58rem',
              letterSpacing: '0.06em',
              height: collapsed ? 14 : 18,
              '& .MuiChip-icon': { color: 'inherit', ml: collapsed ? '4px' : undefined },
              '& .MuiChip-label': { px: collapsed ? 0 : 0.75 },
              minWidth: collapsed ? 14 : undefined,
              px: collapsed ? 0 : undefined,
            }}
          />
        )}

        {!collapsed && <Box sx={{ flexGrow: 1 }} />}

        {/*
          The collapse toggle, expanded only; collapsed, the logo above does the
          job. It used to be a `position: fixed` button parked at
          `left: sidenavWidth - 14`, straddling the seam it operates, the only
          round thing up there, and owned by AppLayout rather than by the nav it
          collapses. It belongs in the bar, on the edge it moves.
        */}
        {!collapsed && (
          <Tooltip title="Collapse sidebar" placement="right">
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              aria-label="Collapse sidebar"
              sx={(t) => ({
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: ROW_RADIUS,
                color: t.palette.text.secondary,
                '&:hover': {
                  bgcolor: t.palette.action.hover,
                  color: t.palette.accent[accentKey].main,
                },
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
              })}
            >
              <ChevronLeftRounded />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/*
        The expand control, collapsed only: a row of its own rather than a
        second thing crammed into a 72px bar beside the mark.

        It drops a line instead of sharing one. That keeps the logo centred,
        keeps the affordance visible without a hover to find it, and gives it the
        same shape as every other row in the rail: one centred icon. It does move
        between states (header when expanded, here when collapsed), which is the
        price of a rail this narrow, and the cheaper half of the trade.
      */}
      {collapsed && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.25 }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              aria-label="Expand sidebar"
              sx={(t) => ({
                width: 32,
                height: 32,
                borderRadius: ROW_RADIUS,
                color: t.palette.text.secondary,
                '&:hover': {
                  bgcolor: t.palette.action.hover,
                  color: t.palette.accent[accentKey].main,
                },
                '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
              })}
            >
              <ChevronRightRounded />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* ── Sections ── */}
      {/*
        No rules between sections any more. A full-width divider, a capitalised
        title and a 24px gutter are three ways of saying the same thing, and the
        eleven client items ended up looking like five lists rather than one nav.
        The title and the space say it once.

        Collapsed there is no title to carry it, so the gap is all there is,
        which is why the top padding stays on the section wrapper rather than
        moving onto the (hidden) heading.
      */}
      {sections.map((section, i) => (
        <Box key={i} sx={{ pt: i > 0 ? 1.5 : collapsed ? 1.25 : 2.5 }}>
          {section.title && !collapsed && (
            <Typography
              variant="overline"
              sx={{
                px: 2.25,
                mb: 0.25,
                display: 'block',
                color: 'text.secondary',
                letterSpacing: '0.07em',
                fontSize: '0.6875rem',
                lineHeight: 2,
              }}
            >
              {section.title}
            </Typography>
          )}
          <List component="nav" sx={{ px: collapsed ? 1 : 1.25 }} disablePadding>
            {section.items.map(renderItem)}
          </List>
        </Box>
      ))}

      <Box sx={{ flexGrow: 1 }} />

      {/*
        ── Footer: who you are, and which zone you are in ──

        Both used to live at the far right of the TopBar, stacked with the
        notification bell and no grouping, which is what made that bar a drawer
        for anything without a home, and left the nav's own foot empty. Identity
        is permanent, so it belongs to the permanent surface; the TopBar is left
        with what changes from page to page.

        "Back to client" is gone with them: the switch is the round trip, and a
        one-way button beside a two-way control was the same journey twice.
      */}
      <Box
        sx={{
          borderTop: (t) => `1px solid ${t.palette.surface.chromeBorder}`,
          // A height, not a minimum: with a minimum the foot grew by a pixel
          // wherever its text rendered taller, and its rule left New List's.
          height: SHELL_FOOT_HEIGHT,
          flexShrink: 0,
          boxSizing: 'border-box',
          justifyContent: 'center',
          px: collapsed ? 1 : 1.5,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        {isAdmin && (
          <Tooltip
            title={collapsed ? (isAdminMode ? 'Leave admin mode' : 'Enter admin mode') : ''}
            placement="right"
          >
            <ListItemButton
              onClick={() => navigate(isAdminMode ? paths.home : paths.admin.licences.root)}
              aria-label={isAdminMode ? 'Leave admin mode' : 'Enter admin mode'}
              sx={{
                borderRadius: ROW_RADIUS,
                py: 0.75,
                px: 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                ...(isAdminMode && {
                  bgcolor: (t) => t.palette.accent.admin.bg,
                  color: (t) => t.palette.accent.admin.main,
                }),
                '&:hover': {
                  bgcolor: (t) =>
                    isAdminMode ? t.palette.accent.admin.hover : t.palette.action.hover,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 36,
                  justifyContent: 'center',
                  color: (t) => (isAdminMode ? t.palette.accent.admin.main : t.palette.text.secondary),
                }}
              >
                <AdminPanelSettingsRounded fontSize="small" />
              </ListItemIcon>
              {!collapsed && (
                <>
                  <ListItemText
                    primary="Admin mode"
                    slotProps={{
                      primary: {
                        sx: {
                          fontSize: '0.875rem',
                          fontWeight: isAdminMode ? 600 : 500,
                          // Inherited from the row, which is amber when the mode is on.
                          color: 'inherit',
                        },
                      },
                    }}
                  />
                  {/* Decoration: the row is the control, so the switch must not
                      be a second target that fires the same navigation twice. */}
                  <Switch
                    size="small"
                    checked={isAdminMode}
                    tabIndex={-1}
                    sx={(t) => ({
                      pointerEvents: 'none',
                      // The thumb defaulted to `primary`: a teal disc on an amber
                      // track, the one place the two accents met and argued.
                      '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                        color: t.palette.accent.admin.main,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: `${t.palette.accent.admin.main} !important`,
                        opacity: '0.45 !important',
                      },
                    })}
                  />
                </>
              )}
            </ListItemButton>
          </Tooltip>
        )}

        {/*
          The build, and nothing else. The foot of a nav is where a product puts
          what is true of the whole session rather than of the page: the zone
          you are in, and the version you are in it with. Identity went back up
          beside the bell, which is where people reach for it.
        */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 0.5,
            height: 28,
          }}
        >
          <Tooltip title={collapsed ? `Version ${APP_VERSION}` : ''} placement="right">
            <Typography
              variant="caption"
              noWrap
              sx={{ color: 'text.disabled', fontSize: '0.6875rem', letterSpacing: '0.02em' }}
            >
              {collapsed ? APP_VERSION : `Version ${APP_VERSION}`}
            </Typography>
          </Tooltip>
        </Box>
      </Box>

    </Drawer>
  );
}
